import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const YourTrainingAndDevelopment = () => (
  <Page
    title="Your Training & Development"
    intro="Your mandatory training, e-learning, appraisals, occupational
    health screenings, and the feedback from audits of records you were
    involved in — all the pages where you keep yourself in date and read what
    comes back."
  >
    <Section title="My Training">
      <p>
        <DocLink text="My Training" url="/mandatory-training" /> lists every
        mandatory course your role needs, each with a status: in date,{' '}
        <strong>Expiring Soon</strong> (the last six weeks before the expiry
        date), overdue, not completed, or pending approval. You can evidence a
        course two ways:
      </p>
      <Bullets>
        <Bullet>
          <strong>Upload a certificate</strong> — it counts once Personnel
          has approved it, and shows as Pending Approval until then.
        </Bullet>
        <Bullet>
          <strong>Take the e-learning</strong> — where a course has an
          AmbuNet e-learning course, choose <strong>Start course</strong>.
        </Bullet>
      </Bullets>
      <p>
        E-learning opens in its own screen. Progress saves on every screen,
        so you can stop and carry on later on any device, and{' '}
        <strong>Next</strong> only unlocks once a screen has been read or its
        activity is done. Each course ends with an assessment:
      </p>
      <Bullets>
        <Bullet>
          The pass mark is <strong>80%</strong> and is shown before you
          start. Questions and answer order change on every attempt.
        </Bullet>
        <Bullet>
          After an unsuccessful attempt you're shown which modules to look at
          again, then you take a new attempt with fresh questions — there is
          no limit on attempts.
        </Bullet>
        <Bullet>
          Passing adds the completion to your record straight away and
          issues a PDF certificate with a verification code and QR code.
        </Bullet>
      </Bullets>
      <Info title="Reminders — and why staying in date matters">
        You're reminded before training expires at the stages your
        organisation configures — by default 90, 30, and 7 days before, and
        on the day. How the reminders reach you follows your notification
        preferences in <DocLink text="My Account" url="/my-account" />. If
        your organisation has switched it on, expired mandatory training also
        blocks you from requesting shifts until you're back in date.
      </Info>
    </Section>

    <Section title="My Appraisals">
      <p>
        When an appraisal is due, it appears on{' '}
        <DocLink text="My Appraisals" url="/my-appraisals" /> with your steps
        to work through in order:
      </p>
      <Bullets>
        <Bullet>
          <strong>Occupational health</strong> — each appraisal starts by
          showing a summary of your last screening. Confirm nothing has
          changed, or go to the full form to update your details.
        </Bullet>
        <Bullet>
          <strong>Pre-appraisal</strong> — a short form before the main
          self-review.
        </Bullet>
        <Bullet>
          <strong>Self-review</strong> — your own reflection on the period.
        </Bullet>
      </Bullets>
      <p>
        Once your steps are done, the appraisal moves to{' '}
        <strong>Awaiting HR Review</strong>: a reviewer completes the HR
        review and agrees objectives with you, and the finished appraisal
        stays viewable on the same page.
      </p>
    </Section>

    <Section title="Occupational health screening">
      <p>
        <DocLink text="Occupational Health" url="/my-occupational-health" />{' '}
        is the standalone screening form. It's where you go when your
        organisation asks you to complete a new screening, when you've never
        completed one, or when an appraisal sends you there to update your
        details. The page tells you when a screening is due from you.
      </p>
    </Section>

    <Section title="My Audits">
      <p>
        When the Governance team audits a patient record you were involved
        in, the outcome appears on{' '}
        <DocLink text="My Audits" url="/my-audits" /> along with any feedback
        the auditor left for you. Open an audit to read the feedback, and —
        if the audited record is yours — mark it as{' '}
        <strong>Reviewed</strong> so your governance team can see you've seen
        it.
      </p>
    </Section>

    <Section title="For clinicians: DOPS and your logbook">
      <p>
        Registered clinicians also have{' '}
        <DocLink text="DOPS &amp; Logbook" url="/dops" /> for directly
        observed procedures and their clinical logbook — see{' '}
        <DocLink
          docsPage
          url="/clinical-records/dops-and-logbook"
          text="DOPS &amp; Logbook"
        />
        .
      </p>
    </Section>

    <Section title="Who can do this?">
      <Info title="Who can do this?">
        Any signed-in staff member has My Training, My Appraisals,
        Occupational Health, and My Audits for their own record. The
        e-learning courses need your organisation to be on the Premium plan
        or above, and occupational health only appears where your
        organisation has enabled it. Managing courses and the training
        matrix? See{' '}
        <DocLink
          docsPage
          url="/employees-hr/mandatory-training-and-e-learning"
          text="Mandatory Training &amp; E-learning"
        />
        .
      </Info>
    </Section>
  </Page>
)

export default YourTrainingAndDevelopment
